import React, { useRef, useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { motion } from "framer-motion";

/* ---------- CARD COMPONENT WITH FLIP ---------- */
const accentColors = [
  "linear-gradient(90deg, #16a34a 0%, #22c55e 50%, #16a34a 100%)",
  "linear-gradient(90deg, #2563eb 0%, #3b82f6 50%, #60a5fa 100%)",
  "linear-gradient(90deg, #f59e0b 0%, #fbbf24 50%, #fcd34d 100%)",
  "linear-gradient(90deg, #db2777 0%, #ec4899 50%, #f472b6 100%)",
  "linear-gradient(90deg, #8b5cf6 0%, #a78bfa 50%, #c4b5fd 100%)",
];

const ProjectCard = ({ title, voltage, client, location, scope, index }) => {
  const accent = accentColors[index % accentColors.length];

  return (
    <Box
      sx={{
        width: 320,
        height: 400,
        flex: "0 0 auto",
        perspective: 1200,
      }}
    >
      <motion.div
        whileHover={{ rotateY: 180, scale: 1.05 }}
        transition={{ duration: 0.6 }}
        style={{
          width: "100%",
          height: "100%",
          position: "relative",
          transformStyle: "preserve-3d",
        }}
      >
        {/* FRONT */}
        <Box
          sx={{
            position: "absolute",
            width: "100%",
            height: "100%",
            borderRadius: 3,
            backgroundImage: "url('/homepage/solar.png')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-end",
            color: "#fff",
            fontWeight: 700,
            textAlign: "center",
            backfaceVisibility: "hidden",
            boxShadow: "0 8px 20px rgba(0,0,0,0.25)",
            overflow: "hidden",
          }}
        >
          {/* Gradient overlay */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(to top, rgba(0,0,0,0.7), rgba(0,0,0,0.2))",
            }}
          />
          {/* Title container */}
          <Box sx={{ position: "relative", p: 3, zIndex: 2 }}>
            <Typography
              variant="h6"
              fontWeight={800}
              sx={{ fontSize: "1.2rem", lineHeight: 1.2 }}
            >
              {title}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.85 }}>
              {client}
            </Typography>
          </Box>
        </Box>

        {/* BACK */}
        <Box
          sx={{
            position: "absolute",
            width: "100%",
            height: "100%",
            borderRadius: 3,
            backgroundColor: "#fff",
            border: "1px solid #e0e0e0",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            p: 3,
            overflowY: "auto",
            backfaceVisibility: "hidden",
            transform: "rotateY(180deg)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Top accent bar */}
          <Box sx={{ height: 6, background: accent, borderRadius: 2, mb: 2 }} />

          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <b>Voltage:</b> {voltage}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <b>Client:</b> {client}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <b>Location:</b> {location}
          </Typography>
          <Box
            sx={{
              mt: 1.5,
              pt: 1,
              borderTop: "1px dashed #d1d5db",
              fontSize: "0.85rem",
              color: "#374151",
              flexGrow: 1,
            }}
          >
            <b>Scope:</b>
            <br />
            {scope}
          </Box>
        </Box>
      </motion.div>
    </Box>
  );
};

/* ---------- PROJECT SECTION COMPONENT (Draggable Carousel) ---------- */
const ProjectSection = ({ title, projects }) => {
  const carouselRef = useRef(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    setWidth(carouselRef.current.scrollWidth - carouselRef.current.offsetWidth);
  }, []);

  return (
    <Box sx={{ px: { xs: 2, md: 6 }, py: 6 }}>
      <Typography variant="h4" fontWeight={900} mb={3}>
        {title}
      </Typography>

      {/* Draggable container */}
      <motion.div
        ref={carouselRef}
        drag="x"
        dragConstraints={{ left: -width, right: 0 }}
        whileTap={{ cursor: "grabbing" }}
        style={{ display: "flex", gap: 20, cursor: "grab" }}
      >
        {projects.map((p, i) => (
          <ProjectCard key={i} {...p} index={i} />
        ))}
      </motion.div>
    </Box>
  );
};

/* ---------- MAIN COMPONENT ---------- */
const Projects = () => {
  const completedProjects = [
    {
      title: "PGCIL – GIS Substation, Shikrapur",
      voltage: "765 / 400 / 220 kV",
      client: "PGCIL",
      location: "Pune, Maharashtra",
      scope:
        "GIS erection, testing & commissioning, 220 kV cable trenching, laying & terminations, 400 kV GIS bay extensions, switchyard works, civil works including PEB buildings, cable trenches and earthing systems.",
    },
    {
      title: "PGCIL – GIS Bay Extensions, Gorakhpur",
      voltage: "400 kV",
      client: "PGCIL",
      location: "Gorakhpur, Uttar Pradesh",
      scope:
        "400 kV GIS bay extension works, switchyard erection, testing & commissioning with utility-grade compliance and safety execution.",
    },
    {
      title: "PGCIL – GIS Substation, Jeypore",
      voltage: "400 kV",
      client: "PGCIL",
      location: "Jeypore, Odisha",
      scope:
        "GIS equipment erection & commissioning, associated switchyard and earthing works.",
    },
    {
      title: "Adani Green Energy – Khavda (PSS-10)",
      voltage: "400 / 33 kV",
      client: "Adani Green Energy Ltd.",
      location: "Khavda, Gujarat",
      scope:
        "Complete ETC works for 400 kV GIS substation, earthing system execution, 33 kV cable laying, termination & dressing, renewable evacuation switchyard works.",
    },
    {
      title: "Adani Green Energy – Khavda (PSS-11)",
      voltage: "400 / 33 kV",
      client: "Adani Green Energy Ltd.",
      location: "Khavda, Gujarat",
      scope:
        "End-to-end ETC for 400 kV GIS substation, earthing works, 33 kV cable laying & terminations, switchyard erection and commissioning.",
    },
    {
      title: "TGSPDCL – Lumbini Park Substation",
      voltage: "33 / 11 kV",
      client: "TGSPDCL",
      location: "Hyderabad, Telangana",
      scope:
        "Electrical equipment erection, 33/11 kV switchgear installation and commissioning.",
    },
    {
      title: "TGSPDCL – Substation Automation (RTU)",
      voltage: "33 / 11 kV",
      client: "TGSPDCL",
      location: "Hyderabad, Telangana",
      scope:
        "SITC of RTU panels, SCADA-ready automation integration at Nagole and Shilparamam substations.",
    },
  ];

  const ongoingProjects = [
    {
      title: "400/33 kV AIS Substation – Complete EPC",
      voltage: "400 / 33 kV",
      client: "M/s PVR Powercon (Renew Power)",
      location: "India",
      scope:
        "Complete EPC execution including civil, electrical, testing & commissioning works for utility-scale AIS substation.",
    },
    {
      title: "JUSNL – 132 kV Bay Extension & Transformer Erection",
      voltage: "132 kV",
      client: "JUSNL",
      location: "Sindri, Jharkhand",
      scope:
        "132 kV bay extension works, power transformer erection and associated switchyard activities.",
    },
    {
      title: "JUSNL – 400 kV Line Differential Protection",
      voltage: "400 kV",
      client: "JUSNL",
      location: "Jharkhand",
      scope:
        "SITC of 400 kV line differential protection scheme including testing, integration and commissioning.",
    },
    {
      title: "Adani Green Energy – Khavda (PSS-7)",
      voltage: "400 / 33 kV",
      client: "Adani Green Energy Ltd.",
      location: "Khavda, Gujarat",
      scope:
        "Complete ETC works for 400 kV GIS substation, earthing, 33 kV cable laying & terminations, switchyard execution for renewable evacuation.",
    },
    {
      title: "TGSPDCL – RTU & Protection Retrofit",
      voltage: "33 / 11 kV",
      client: "TGSPDCL (EPC: Sealwel Corporation)",
      location: "Telangana",
      scope:
        "ETC of Siemens RTU panels, protection relay retrofit, SCADA-ready substation upgrades.",
    },
  ];

  return (
    <Box sx={{ backgroundColor: "#f3f4f6" }}>
      {/* HERO */}
      <Box
        sx={{
          height: { xs: "40vh", md: "50vh" },
          backgroundImage: "url('/pro.jpg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          position: "relative",
        }}
      >
        <Box
          sx={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.6)" }}
        />
        <Box sx={{ position: "relative", zIndex: 2 }}>
          <Typography variant="h2" fontWeight={900} color="#fff">
            Project Portfolio
          </Typography>
          <Typography sx={{ mt: 2, color: "#e5e7eb", fontSize: "1.1rem" }}>
            Utility-scale EPC • GIS • AIS • ETC • Automation
          </Typography>
        </Box>
      </Box>

      <ProjectSection title="Completed Projects" projects={completedProjects} />
      <ProjectSection title="Ongoing Projects" projects={ongoingProjects} />
    </Box>
  );
};

export default Projects;